import { useDirection } from "@radix-ui/react-direction";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Globe,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  LegalDocumentRequirementScope,
  useListMyLegalDocumentsParticipantsMeLegalDocumentsGet,
} from "@/api-v3";
import { AppVersion } from "@/components/app-version";
import { CardContainer } from "@/components/card-container";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { ErrorRetryComponent } from "@/components/error-card/error-retry";
import { Separator } from "@/components/ui/separator";
import { useLogout } from "@/features/auth";
import { ContractsLoadingComponent } from "@/features/auth/components/auth-form/contracts";
import { APP_NAME, useTenantDisplayName } from "@/features/branding";
import { getContractLabelText } from "@/features/legal";
import { LanguageButton } from "@/features/localisation";

import { AutoAcceptSetting } from "./components/auto-accept-setting";

export const SettingsPage = () => {
  const { t } = useTranslation();
  const direction = useDirection();
  const signout = useLogout();

  const {
    data: contracts,
    isLoading: isContractsLoading,
    isError: hasContractsErrored,
    refetch: refetchContracts,
  } = useListMyLegalDocumentsParticipantsMeLegalDocumentsGet({
    status: "accepted",
  });

  const clientName = useTenantDisplayName() ?? APP_NAME;
  const [confirmLogoutDialog, setConfirmLogoutDialog] =
    useState<boolean>(false);

  const handleOpenLogoutDialog = () => {
    setConfirmLogoutDialog(true);
  };

  const handleCloseLogoutDialog = () => {
    setConfirmLogoutDialog(false);
  };

  return (
    <>
      <CardContainer>
        <CardContainer.Header>
          <CardContainer.HeaderContent>
            <CardContainer.CardTitle>{t("settings")}</CardContainer.CardTitle>
          </CardContainer.HeaderContent>
        </CardContainer.Header>
        <CardContainer.Content>
          <nav>
            <Link
              className="flex items-center gap-x-4 py-2"
              data-test-id="change-pin"
              to="/settings/change-pin"
            >
              <Settings />

              <div className="leading-loose flex-1">
                {t("settings_changePin")}
              </div>

              {direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
            </Link>
            <div
              data-test-id="language"
              className="flex items-center gap-x-4 py-2"
            >
              <Globe />
              <div className="leading-loose flex-1">
                {t("settings_changeLanguage")}
              </div>
              <LanguageButton variant="settings" />
            </div>
            <AutoAcceptSetting />
          </nav>
          <Separator className="my-2" />
          {hasContractsErrored ? (
            <div className="flex items-center justify-start">
              <ErrorRetryComponent
                className="items-center"
                retry={refetchContracts}
              />
            </div>
          ) : (
            <>
              {isContractsLoading ? (
                <div className="w-52">
                  <ContractsLoadingComponent tickbox={false} />
                </div>
              ) : (
                <nav>
                  {contracts
                    ?.filter(
                      (val) =>
                        val.required_for ===
                        LegalDocumentRequirementScope.ALL_USERS
                    )
                    .map((contract) => {
                      const label = getContractLabelText(
                        contract.title,
                        clientName,
                        t
                      );

                      if (contract.version.document_url != null) {
                        return (
                          <a
                            key={contract.id}
                            className="leading-loose flex items-center gap-x-2 underline"
                            href={contract.version.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {label}
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        );
                      }

                      return (
                        <span
                          key={contract.id}
                          className="leading-loose flex items-center gap-x-2"
                        >
                          {label}
                        </span>
                      );
                    })}
                </nav>
              )}
            </>
          )}
          <button
            className="block text-red-500 py-2 leading-loose w-full text-left"
            data-test-id="logout"
            onClick={handleOpenLogoutDialog}
          >
            {t("settings_logout")}
          </button>
        </CardContainer.Content>
      </CardContainer>

      <AppVersion className="mt-4" />

      <ConfirmationDialog
        onOpenChange={setConfirmLogoutDialog}
        label="logout-confirmation-dialog"
        open={confirmLogoutDialog}
        title={t("settings_logout_confirmation_title")}
        message={t("settings_logout_confirmation_description")}
        confirmButtonText={t("settings_logout")}
        cancelButtonText={t("cancel")}
        onConfirm={signout}
        onDismiss={handleCloseLogoutDialog}
      />
    </>
  );
};
